import React, { useEffect, useRef, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { MdAdd, MdInfoOutline, MdMoreHoriz } from 'react-icons/md';
import CreateCard from '../../../forms/CreateCard';
import { formatCurrency } from '../utils';
import NewKanbanCard from './NewKanbanCard';

function NewKanbanColumn({ column, columnData, summary, kanbanReady, loadMoreColumnCards, canViewValue, showValues, cardActions, density }) {
  const [creating, setCreating] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const bodyRef = useRef(null);
  const loadingRef = useRef(false);
  const cards = columnData?.cards || [];
  const count = summary ? Number(summary.count || 0) : Number(columnData?.totalCount || cards.length);
  const total = summary ? Number(summary.total_value || 0) : Number(columnData?.totalValue || 0);
  const totalLabel = !canViewValue ? 'Restrito' : showValues ? formatCurrency(total) : '••••••';

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    const onScroll = async () => {
      if (loadingRef.current || !columnData?.hasMore) return;
      if (body.scrollTop + body.clientHeight < body.scrollHeight - 32) return;
      loadingRef.current = true;
      setLoadingMore(true);
      try { await loadMoreColumnCards(column.id); } finally {
        loadingRef.current = false;
        setLoadingMore(false);
      }
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, [column.id, columnData?.hasMore, loadMoreColumnCards]);

  return (
    <Droppable droppableId={String(column.id)}>
      {(provided, snapshot) => (
        <section
          className={`new-home-column ${snapshot.isDraggingOver ? 'is-dragging-over' : ''}`}
          ref={provided.innerRef}
          {...provided.droppableProps}
          aria-label={`Etapa ${column.name}`}
        >
          <header className="new-home-column-header">
            <div className="new-home-column-heading">
              <span className="new-home-column-accent" style={{ background: column.color || undefined }} />
              <div>
                <h2>{column.name}</h2>
                <p>{count} {count === 1 ? 'oportunidade' : 'oportunidades'}</p>
              </div>
              {column.description && <button className="new-home-icon-button" onClick={() => setShowDescription((value) => !value)} aria-label={`Informações sobre ${column.name}`} title="Informações da etapa"><MdInfoOutline /></button>}
              <button className="new-home-icon-button" aria-label={`Mais opções de ${column.name}`} title="Mais opções"><MdMoreHoriz /></button>
            </div>
            <div className="new-home-column-summary">
              <div><span>Total</span><strong title={showValues ? formatCurrency(total) : canViewValue ? 'Valor oculto' : 'Restrito'}>{totalLabel}</strong></div>
              <button onClick={() => setCreating(true)} aria-label={`Adicionar card em ${column.name}`}><MdAdd /> Adicionar</button>
            </div>
            {showDescription && <div className="new-home-column-description">{column.description}</div>}
          </header>

          <div className="new-home-column-body" ref={bodyRef}>
            {!columnData && !kanbanReady ? (
              <div className="new-home-column-loading" aria-live="polite"><span /><span /><span /><small>Carregando etapa…</small></div>
            ) : cards.length ? (
              cards.map((card, index) => <NewKanbanCard key={card.card_id} card={card} index={index} actions={cardActions} density={density} />)
            ) : (
              <div className="new-home-empty-column"><span>✓</span><strong>Etapa em dia</strong><small>Nenhuma oportunidade com os filtros atuais.</small><button onClick={() => setCreating(true)}>Criar primeiro card</button></div>
            )}
            {loadingMore && <div className="new-home-load-more" aria-live="polite">Carregando mais…</div>}
            {provided.placeholder}
          </div>
          <footer className="new-home-column-footer"><span>Etapa {column.id}</span><span>{cards.length} de {count}</span></footer>
          {creating && <CreateCard columnId={column.id} onClose={() => setCreating(false)} />}
        </section>
      )}
    </Droppable>
  );
}

export default React.memo(NewKanbanColumn);
